import type { ReactNode } from 'react';
import { Icone } from '@/shared/ui';

/**
 * Exportado para o teste de acessibilidade do trilho.
 *
 * O `AREAS` e o `GESTAO` saem pela porta do widget pelo mesmo motivo: o que o menu promete
 * — nome para quem não vê o texto, estado do item ativo, o aviso de mensagens —
 * é verificável neste componente, e não dentro da casca inteira, que exigiria
 * sessão, consultas e roteamento para medir um `aria-label`.
 */
export function ItemDoMenu({
  nome,
  icone,
  ativo,
  recolhido,
  aviso,
  emBreve,
  submenu,
  aoAbrir,
}: {
  nome: string;
  icone: ReactNode;
  ativo?: boolean;
  /** Recolhido, o nome sai da tela e vira a dica do navegador. */
  recolhido?: boolean;
  /**
   * A bolinha no canto do ícone — o aviso do menu RECOLHIDO.
   *
   * Sem número de propósito: o que cabe em 7px é a existência do aviso, não a
   * quantidade. Quem quer o número abre o menu, e lá está o sino.
   */
  aviso?: boolean;
  /**
   * O selo "Em breve" à direita do nome. Ver o comentário de `AREAS` sobre
   * por que ele existe para um item só.
   *
   * O item continua abrindo: o selo qualifica o que a tela mostra, não trava o
   * clique. Recolhido, ele some com o nome, e o rótulo continua dizendo.
   */
  emBreve?: boolean;
  /**
   * O item tem submenu: a seta à direita diz se ele está aberto ou fechado, e
   * o botão diz o mesmo pelo `aria-expanded`. Sem submenu, nem seta nem estado.
   */
  submenu?: 'aberto' | 'fechado';
  aoAbrir?: () => void;
}): ReactNode {
  /*
   * O selo entra no rótulo porque o `aria-label` substitui o texto do botão:
   * sem isto, quem usa leitor de tela ouviria "Automações" num item que, na
   * tela, diz "Automações Em breve".
   */
  const rotulo = aviso
    ? `${nome} — mensagens esperando resposta`
    : emBreve
      ? `${nome}, em breve`
      : nome;

  return (
    /*
      `aria-label` com o nome, sempre — não só quando recolhido.
      Com o menu fechado o texto some da árvore e o ícone é `aria-hidden`: sem
      o rótulo, o botão viraria "botão" para quem usa leitor de tela. Deixá-lo
      fixo evita um atributo que aparece e some conforme o estado da interface,
      que é como um se esquece do outro.

      E o nome precisa estar num `<span>`: texto solto não é selecionável por
      CSS, então não haveria como escondê-lo ao recolher.
    */
    <button
      className="casca__item"
      type="button"
      disabled={!aoAbrir}
      aria-current={ativo ? 'page' : undefined}
      aria-expanded={submenu ? submenu === 'aberto' : undefined}
      /*
        Com a bolinha, o rótulo DIZ o que ela quer dizer. Ela é um ponto de
        7px sem texto: para quem usa leitor de tela, ela não existiria.
      */
      aria-label={rotulo}
      {...(recolhido ? { title: rotulo } : {})}
      onClick={aoAbrir}
    >
      <span className="casca__icone" aria-hidden="true">
        {icone}
        {aviso && <span className="casca__aviso" />}
      </span>
      <span className="casca__nome">{nome}</span>
      {emBreve && (
        <span className="casca__breve" aria-hidden="true">
          Em breve
        </span>
      )}
      {/* Recolhido, a seta sai do DOM: no trilho estreito ela empurrava o ícone. */}
      {submenu && !emBreve && !recolhido && (
        <span
          className="casca__seta"
          data-aberto={submenu === 'aberto' ? 'sim' : undefined}
          aria-hidden="true"
        >
          <Icone nome="abaixo" />
        </span>
      )}
    </button>
  );
}
